import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback, useRef } from 'react';
import { api, getUserId } from '@/lib/api';
import QuizTimer from '@/components/QuizTimer';
import { toast } from 'sonner';

export default function QuizPlayPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const level = location.state?.level;
  const challengeId = location.state?.challengeId;

  const [runId, setRunId] = useState(null);
  const [question, setQuestion] = useState(null);
  const [timePerQ, setTimePerQ] = useState(20);
  const [timerKey, setTimerKey] = useState(0);
  const [selected, setSelected] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const questionStartRef = useRef(Date.now());
  const answeredRef = useRef(false);

  // Start run
  useEffect(() => {
    if (!level && !challengeId) { navigate('/'); return; }
    const startRun = async () => {
      try {
        const userId = getUserId();
        let res;
        if (challengeId) {
          res = await api.post('/challenge/start', { challengeId, userId });
        } else {
          res = await api.post('/run/start', { userId, level });
        }
        const d = res.data;
        setRunId(d.runId);
        setTimePerQ(d.timePerQuestionSeconds);
        setQuestion(d.firstQuestionPayload);
        setTimerKey(k => k + 1);
        questionStartRef.current = Date.now();
        answeredRef.current = false;
      } catch (e) {
        const detail = e.response?.data?.detail;
        if (detail && typeof detail === 'object' && detail.unlocked === false) {
          navigate('/');
        } else {
          toast.error('Fehler beim Starten');
          navigate('/');
        }
      }
    };
    startRun();
  }, [level, challengeId, navigate]);

  const submitAnswer = useCallback(async (optIndex, timedOut = false) => {
    if (answeredRef.current || !runId || !question) return;
    answeredRef.current = true;
    const timeSpent = timedOut ? timePerQ * 1000 : Date.now() - questionStartRef.current;
    setTransitioning(true);
    try {
      const res = await api.post('/run/answer', {
        runId, questionId: question.questionId,
        selectedOptionIndex: timedOut ? null : optIndex,
        timeSpentMs: Math.min(timeSpent, timePerQ * 1000),
        timedOut
      });
      const d = res.data;
      setTimeout(() => {
        if (d.nextQuestionPayload) {
          setQuestion(d.nextQuestionPayload);
          setSelected(null);
          setTransitioning(false);
          setTimerKey(k => k + 1);
          questionStartRef.current = Date.now();
          answeredRef.current = false;
        } else {
          completeRun();
        }
      }, 300);
    } catch (e) {
      toast.error('Fehler beim Antworten');
      setTransitioning(false);
      answeredRef.current = false;
    }
  }, [runId, question, timePerQ]);

  const completeRun = async () => {
    try {
      const res = await api.post('/run/complete', { runId });
      if (challengeId) {
        navigate('/challenge/result', { state: { result: res.data, challengeId, runId } });
      } else {
        navigate('/quiz/result', { state: { result: { ...res.data, runId }, runId } });
      }
    } catch (e) {
      toast.error('Fehler beim Abschließen');
    }
  };

  const handleTimeout = useCallback(() => {
    submitAnswer(null, true);
  }, [submitAnswer]);

  const handleOptionClick = (idx) => {
    if (answeredRef.current || transitioning) return;
    setSelected(idx);
    submitAnswer(idx);
  };

  if (!question) {
    return (
      <div className="flex-1 flex items-center justify-center mesh-bg">
        <div className="text-[#9CA3AF] animate-pulse font-outfit text-xl">Quiz wird geladen...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center px-4 py-2 sm:py-0" data-testid="quiz-play-page">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 sm:mb-6">
          <div>
            <span className="text-[#9CA3AF] text-xs sm:text-sm font-mono">
              Frage {question.questionNumber}/{question.totalQuestions}
            </span>
            {question.category && (
              <span className="ml-2 sm:ml-3 px-2 py-0.5 text-[10px] sm:text-xs rounded-full bg-[#3B82F6]/15 text-[#60A5FA] font-mono">
                {question.category}
              </span>
            )}
          </div>
          <QuizTimer
            totalSeconds={timePerQ}
            onTimeout={handleTimeout}
            resetKey={timerKey}
          />
        </div>

        {/* Progress bar */}
        <div className="w-full h-1 bg-white/5 rounded-full mb-4 sm:mb-8 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] rounded-full transition-all duration-500"
            style={{ width: `${(question.questionNumber / question.totalQuestions) * 100}%` }}
          />
        </div>

        {/* Question */}
        <div className="glass-card rounded-2xl p-4 sm:p-8 mb-4 sm:mb-6 animate-fade-up">
          <h2 className="font-outfit font-bold text-lg sm:text-2xl text-white leading-relaxed" data-testid="question-text">
            {question.questionText}
          </h2>
        </div>

        {/* Options */}
        <div className="grid gap-2 sm:gap-3">
          {question.options.map((opt, idx) => (
            <button
              key={idx}
              onClick={() => handleOptionClick(idx)}
              disabled={transitioning}
              data-testid={`option-${idx}`}
              className={`w-full text-left p-3 sm:p-5 rounded-xl border transition-all duration-200 font-manrope text-sm sm:text-base
                ${selected === idx
                  ? 'border-[#3B82F6] bg-[#3B82F6]/15 text-white'
                  : 'border-white/8 bg-white/3 text-[#E5E7EB] hover:bg-white/8 hover:border-white/15'
                }
                ${transitioning ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:-translate-y-0.5'}
              `}
            >
              <span className="inline-flex items-center justify-center w-6 sm:w-7 h-6 sm:h-7 rounded-md bg-white/10 text-[10px] sm:text-xs font-mono font-bold mr-2 sm:mr-3 text-[#9CA3AF]">
                {String.fromCharCode(65 + idx)}
              </span>
              {opt}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
